import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BiFilter, BiSearch } from "react-icons/bi";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMInputAdormant from "src/components/UI/atoms/formFields/ATMInputAdormant/ATMInputAdormant";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import OrderFilterDialogWrapper from "./FilterDialog/OrderFilterDialogWrapper";
import { usePermission } from "src/hooks/usePermission";
import ListDateTime from "src/components/UI/atoms/ListDateTime/ListDateTime";

export type StatusTab = "ALL" | "SUCCESS" | "PENDING" | "FAILED" | "CANCELLED";

type Tone = "success" | "pending" | "failed" | "cancel";

type Props = {
  rows: any[];
  statusTab: StatusTab;
  onStatusTabChange: (tab: StatusTab) => void;
  onCancel: (row: any) => void;
  canEdit: boolean;
  paginationProps: {
    isTableLoading: boolean;
    totalItems: number;
    page: number;
    rowsPerPage: number;
    searchValue: string;
    setPage: (newPage: number) => void;
    setRowsPerPage: (newLimit: number) => void;
    setSearchValue: (newValue: string) => void;
  };
};

const STATUS_TABS: { id: StatusTab; label: string }[] = [
  { id: "SUCCESS", label: "Payment Success" },
  { id: "PENDING", label: "Pending" },
  { id: "FAILED", label: "Failed" },
  { id: "CANCELLED", label: "Cancel" },
  { id: "ALL", label: "All" },
];

const STATUS_PILL: Record<StatusTab, { active: string; idle: string }> = {
  SUCCESS: {
    active: "bg-emerald-600 text-white border-emerald-600",
    idle: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  PENDING: {
    active: "bg-amber-400 text-primary-main border-amber-400",
    idle: "bg-amber-50 text-amber-800 border-amber-200",
  },
  FAILED: {
    active: "bg-red-600 text-white border-red-600",
    idle: "bg-red-50 text-red-700 border-red-200",
  },
  CANCELLED: {
    active: "bg-secondary-main text-white border-secondary-main",
    idle: "bg-rose-50 text-secondary-main border-rose-200",
  },
  ALL: {
    active: "bg-primary-main text-white border-primary-main",
    idle: "bg-sky-50 text-primary-main border-sky-200",
  },
};

const BADGE: Record<Tone, string> = {
  success: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  cancel: "bg-rose-100 text-secondary-main",
};

const CARD_ACCENT: Record<Tone, string> = {
  success: "border-l-4 border-l-emerald-500",
  pending: "border-l-4 border-l-amber-400",
  failed: "border-l-4 border-l-red-500",
  cancel: "border-l-4 border-l-secondary-main",
};

const HEADER_TONE = {
  navy: "bg-primary-main text-white",
  gold: "bg-amber-100 text-amber-900",
  green: "bg-emerald-100 text-emerald-800",
};

const textOf = (...values: unknown[]) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    return String(value);
  }
  return "-";
};

export const orderTone = (row: any): Tone => {
  if (row?.isDeleted) return "cancel";
  if (row?.isActive === false) return "failed";
  if (Number(row?.dueAmt) > 0) return "pending";
  return "success";
};

const TONE_LABEL: Record<Tone, string> = {
  success: "Payment Success",
  pending: "Pending",
  failed: "Failed",
  cancel: "Cancel",
};

const money = (value: unknown) =>
  value == null || value === "" ? "-" : `₹${value}`;

const InfoGrid = ({
  headers,
  values,
  tone = "navy",
}: {
  headers: string[];
  values: React.ReactNode[];
  tone?: keyof typeof HEADER_TONE;
}) => (
  <div className="overflow-hidden rounded border border-slate-200 bg-white">
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${headers.length}, minmax(90px, 1fr))`,
      }}
    >
      {headers.map((header) => (
        <div
          key={header}
          className={`${HEADER_TONE[tone]} border-b border-r px-2 py-2 text-sm font-semibold last:border-r-0`}
        >
          {header}
        </div>
      ))}
      {values.map((value, index) => (
        <div
          key={index}
          className="break-words border-r border-slate-200 px-2 py-2 text-sm text-slate-800 last:border-r-0"
        >
          {value || "-"}
        </div>
      ))}
    </div>
  </div>
);

const OrderDetail = ({
  row,
  canEdit,
  onCancel,
  onClose,
}: {
  row: any;
  canEdit: boolean;
  onCancel: (row: any) => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const tone = orderTone(row);
  const products = Array.isArray(row?.products) ? row.products : [];

  return (
    <div className="overflow-hidden rounded-b border border-t-0 border-slate-200 bg-white">
      <div className="flex flex-col xl:flex-row">
        <div className="min-w-0 flex-1 space-y-2 overflow-auto p-3">
          <InfoGrid
            tone="navy"
            headers={[
              "Order No",
              "Name",
              "Mobile",
              "Email",
              "Status",
              "Order Total",
              "Received",
              "Due",
            ]}
            values={[
              textOf(row.orderNumber, row.orderId),
              textOf(row.name),
              textOf(row.mobile),
              textOf(row.email),
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-sm font-semibold ${BADGE[tone]}`}
              >
                {TONE_LABEL[tone]}
              </span>,
              money(row.orderTotal),
              money(row.receivedAmt),
              money(row.dueAmt),
            ]}
          />
          <InfoGrid
            tone="gold"
            headers={[
              "Delivery",
              "City",
              "State",
              "Pincode",
              "Discount",
              "Discount Type",
              "CRO",
            ]}
            values={[
              textOf(row.delivery),
              textOf(row.city),
              textOf(row.state),
              textOf(row.pincode),
              textOf(row.discountValue),
              textOf(row.discountType),
              textOf(row.croNumber),
            ]}
          />
          <InfoGrid
            tone="green"
            headers={["User Type", "Due Date", "Remark", "Ordered On"]}
            values={[
              textOf(row.userType),
              <ListDateTime value={row.duedate} />,
              textOf(row.remark),
              <ListDateTime value={row.createdAt} />,
            ]}
          />
          {products.length ? (
            <div className="rounded border border-slate-200">
              <div className="bg-primary-main px-2 py-2 text-sm font-semibold text-white">
                Products
              </div>
              {products.map((product: any, index: number) => (
                <div
                  key={product?._id || index}
                  className="grid grid-cols-3 gap-2 border-t border-slate-200 px-2 py-2 text-sm text-slate-800"
                >
                  <span>{textOf(product?.productName)}</span>
                  <span>Qty: {textOf(product?.quantity)}</span>
                  <span>{money(product?.price)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="border-t border-slate-200 bg-gradient-to-b from-amber-50 to-white p-3 xl:w-[240px] xl:border-l xl:border-t-0">
          <div className="mb-2 flex justify-end xl:hidden">
            <button
              type="button"
              className="text-sm font-semibold text-secondary-main"
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <p className="mb-3 text-sm font-semibold leading-snug text-primary-main">
            {textOf(products[0]?.productName, row.name)}
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="w-full rounded bg-primary-main py-2 text-sm font-semibold text-white"
              onClick={() => navigate(`edit/${row?._id}/order-deatils`)}
            >
              VIEW ORDER
            </button>
            {canEdit && !row?.isDeleted ? (
              <button
                type="button"
                className="w-full rounded bg-secondary-main py-2 text-sm font-semibold text-white"
                onClick={() => onCancel(row)}
              >
                CANCEL ORDER
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const OfflineOrderListing = ({
  rows,
  statusTab,
  onStatusTabChange,
  onCancel,
  canEdit,
  paginationProps: {
    isTableLoading,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    setPage,
    setRowsPerPage,
    setSearchValue,
  },
}: Props) => {
  const navigate = useNavigate();
  const canAdd = usePermission("ORDER", "ADD");
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <SideNavLayout>
      <div className="flex h-full flex-col bg-surface-medium">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
          {STATUS_TABS.map((tab) => {
            const active = statusTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  onStatusTabChange(tab.id);
                  setExpandedId(null);
                }}
                className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
                  active ? STATUS_PILL[tab.id].active : STATUS_PILL[tab.id].idle
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenFilterDialog(true)}
              className="rounded border border-amber-300 bg-amber-50 px-2 py-2 text-amber-800"
              title="Filter by date"
            >
              <BiFilter className="text-xl" />
            </button>
            <div className="w-[280px] max-w-full">
              <ATMInputAdormant
                name=""
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                adormant={<BiSearch />}
                adormantProps={{
                  position: "start",
                  extraClasses: "bg-white border-0",
                }}
                inputProps={{ className: "bg-white" }}
                placeholder="Search..."
              />
            </div>
          </div>
          {canAdd ? (
            <button
              type="button"
              onClick={() => navigate("add")}
              className="rounded bg-primary-main px-4 py-2 text-sm font-semibold text-white"
            >
              Add New
            </button>
          ) : null}
        </div>

        <div className="flex-1 space-y-3 overflow-auto px-4 pb-2">
          {isTableLoading ? (
            <div className="rounded border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Loading orders...
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              No orders found.
            </div>
          ) : (
            rows.map((row) => {
              const open = expandedId === row?._id;
              const tone = orderTone(row);
              return (
                <div key={row?._id}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(open ? null : row?._id)}
                    className={`w-full border bg-white px-3 py-2 text-left ${CARD_ACCENT[tone]} ${
                      open
                        ? "rounded-t border-b-0 border-primary-light"
                        : "rounded border-slate-200 hover:bg-sky-50/40"
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4 xl:grid-cols-7">
                      <div>
                        <div className="text-slate-400">Order No</div>
                        <div className="break-all font-normal text-primary-main">
                          {textOf(row.orderNumber, row.orderId)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400">User</div>
                        <div className="font-normal capitalize text-slate-800">
                          {textOf(row.name)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400">Mobile</div>
                        <div className="font-normal text-slate-800">
                          {textOf(row.mobile)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400">Status</div>
                        <span
                          className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-sm font-semibold ${BADGE[tone]}`}
                        >
                          {TONE_LABEL[tone]}
                        </span>
                      </div>
                      <div>
                        <div className="text-slate-400">Amount</div>
                        <div className="font-normal text-amber-700">
                          {money(row.orderTotal)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400">Due</div>
                        <div className="font-normal text-slate-800">
                          {money(row.dueAmt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400">Ordered On</div>
                        <ListDateTime value={row.createdAt} />
                      </div>
                    </div>
                  </button>
                  {open ? (
                    <OrderDetail
                      row={row}
                      canEdit={canEdit}
                      onCancel={onCancel}
                      onClose={() => setExpandedId(null)}
                    />
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-slate-200 bg-white">
          <ATMPagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            rowCount={totalItems}
            rows={rows}
          />
        </div>

        {openFilterDialog ? (
          <OrderFilterDialogWrapper
            onClose={() => setOpenFilterDialog(false)}
          />
        ) : null}
      </div>
    </SideNavLayout>
  );
};

export default OfflineOrderListing;
